import React, { Component, memo } from 'react';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import SearchMenu from './searchMenu';
import EditBtnCellRenderer from './editBtnCellRenderer';
import AddFlag from './addFlag';
import { fetchFlags } from '../../../../services/clinicPortalServices/flagService';
import toastr from 'toastr';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { ThemeContext } from '../../../../theme/ThemeProvider';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

export default class FlagGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            columnDefs: [
                {
                    headerName: 'Actions',
                    field: 'actions',
                    cellRenderer: EditBtnCellRenderer,
                    sortable: false,
                    filter: false,
                    resizable: false,
                    minWidth: 100
                },
                {
                    headerName: 'Name',
                    field: 'name',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 120
                },
                {
                    headerName: 'Description',
                    field: 'description',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 200
                },
                {
                    headerName: 'Flag Type',
                    field: 'flagType',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 120,
                    cellRenderer: memo((params) => {
                        const type = params.value || 'general';
                        const typeColors = {
                            toxicology: 'badge-info',
                            validity: 'badge-warning',
                            general: 'badge-secondary'
                        };
                        return <span class={'p-1 badge ' + (typeColors[type] || 'badge-secondary')}>{type}</span>;
                    })
                },
                {
                    headerName: 'Trigger Formula',
                    field: 'triggerFormula',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 250
                },
                {
                    headerName: 'Outcome Rule',
                    field: 'outcomeRule',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 200,
                    cellRenderer: (params) => {
                        if (!params.value) return '-';
                        return params.value;
                    }
                },
                {
                    headerName: 'HL7 Code',
                    field: 'hl7InterpretationCode',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 100,
                    cellRenderer: (params) => {
                        return params.value || '-';
                    }
                },
                {
                    headerName: 'Analytes',
                    field: 'analytes',
                    sortable: false,
                    filter: false,
                    resizable: true,
                    minWidth: 200,
                    cellRenderer: (params) => {
                        if (params.value && params.value.length > 0) {
                            const names = params.value
                                .map((a) => a.name)
                                .slice(0, 3)
                                .join(', ');
                            const more = params.value.length > 3 ? ` +${params.value.length - 3} more` : '';
                            return names + more;
                        }
                    }
                },
                {
                    headerName: 'Created',
                    field: 'createdAt',
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 150,
                    cellRenderer: (params) => {
                        return params.value ? moment(params.value).format('MM/DD/YYYY') : '';
                    }
                }
            ],
            defaultColDef: {
                flex: 1,
                filter: true,
                sortable: true
            },
            rowData: [],
            selectedRows: [],
            isAddFlagOpen: false,
            quickFilterText: ''
        };

        this.gridApi = null;
    }

    componentDidMount() {
        // Grid will load data on ready
    }

    onGridReady = (params) => {
        this.gridApi = params.api;
        this.loadGridData();
    };

    loadGridData = () => {
        fetchFlags()
            .then((res) => {
                if (res && res.success) {
                    this.setState({ rowData: res.data }, () => {
                        // Sort by flag type after data is loaded
                        if (this.gridApi) {
                            this.gridApi.applyColumnState({
                                state: [{ colId: 'flagType', sort: 'asc' }],
                                defaultState: { sort: null }
                            });
                        }
                    });
                }
            })
            .catch((error) => {
                console.error('Error loading flags:', error);
                toastr.error('Failed to load flags');
            });
    };

    onSelectionChanged = () => {
        const selectedRows = this.gridApi.getSelectedRows();
        this.setState({ selectedRows: selectedRows });
    };

    onFilterTextChange = (value) => {
        this.setState({ quickFilterText: value });
    };

    clearFilter = () => {
        this.gridApi.setFilterModel(null);
        this.setState({ quickFilterText: '' });
        document.getElementById('reset-form').value = '';
    };

    handleAddFlag = () => {
        this.setState({ isAddFlagOpen: true });
    };

    handleCloseAddFlag = () => {
        this.setState({ isAddFlagOpen: false });
        this.loadGridData();
    };

    render() {
        return (
            <div className="clinic-contain">
                <SearchMenu
                    searchValue={this.state.quickFilterText}
                    onSearch={this.onFilterTextChange}
                    onAddFlag={this.handleAddFlag}
                    clearFilter={this.clearFilter}
                />
                <div
                    style={{
                        width: '100%',
                        height: 'calc(100vh - 250px)',
                        padding: '15px'
                    }}
                >
                    <ThemeContext.Consumer>
                        {({ themeName }) => (
                            <div
                                id="myGrid"
                                style={{
                                    height: '100%',
                                    width: '100%'
                                }}
                            >
                                <AgGridReact
                                    columnDefs={this.state.columnDefs}
                                    defaultColDef={this.state.defaultColDef}
                                    rowData={this.state.rowData}
                                    onGridReady={this.onGridReady}
                                    onSelectionChanged={this.onSelectionChanged}
                                    rowSelection="multiple"
                                    pagination={true}
                                    paginationAutoPageSize={true}
                                    theme={themeName === 'Light' ? themeLight : themeDark}
                                    quickFilterText={this.state.quickFilterText}
                                    suppressRowClickSelection={true}
                                    context={{
                                        componentParent: this
                                    }}
                                />
                            </div>
                        )}
                    </ThemeContext.Consumer>
                </div>
                {this.state.isAddFlagOpen && (
                    <AddFlag
                        isOpen={this.state.isAddFlagOpen}
                        handleClose={this.handleCloseAddFlag}
                        context={{ componentParent: this }}
                    />
                )}
            </div>
        );
    }
}
